<template>
  <div class="audio-transcribe-wrapper" v-if="isAIFeaturesEnabled">
    <!-- Main audio controls -->
    <div class="audio-controls position-absolute" style="bottom: 5px; right: 5px" v-if="isButtonVisible">
      <!-- Timer display -->
      <div
        v-if="isRecording"
        class="timer-display"
        :class="{ 'timer-warning': remainingTime < 30000 }"
      >
        {{ formatTime(remainingTime) }}
      </div>

      <!-- Button container -->
      <div class="button-container">
        <!-- Original beautiful audio button with badge -->
        <div class="audio-button-wrapper" v-if="!isRecording && !isTranscribing">
          <b-button
            @click="startRecording"
            variant="outline-primary"
            size="sm"
            class="audio-btn"
            ref="audioButton"
            v-b-tooltip.hover.left
            title="เริ่มบันทึกเสียง"
          >
            <i class="fas fa-microphone"></i>
          </b-button>

          <!-- Small badge button for menu -->
          <button class="menu-badge" @click.stop="showContextMenu">
            <i class="fas fa-chevron-up"></i>
          </button>
        </div>

        <!-- Context Menu -->
        <div
          v-if="showMenu"
          class="context-menu"
          @click="hideContextMenu"
          ref="contextMenuOverlay"
        >
          <div class="context-menu-content" @click.stop>
            <div class="context-menu-items">
              <div class="context-menu-item" @click="openHistory">
                <i class="fas fa-history text-info"></i>
                <span>ดูประวัติการแปลงเสียง</span>
              </div>
              <div class="context-menu-item" @click="toggleButtonVisibility">
                <i class="fas fa-compress-alt text-warning"></i>
                <span>ย่อขนาดปุ่มบันทึกเสียง</span>
              </div>
            </div>
          </div>
        </div>

        <b-button
          v-if="isRecording && !isPaused"
          @click="pauseRecording"
          variant="warning"
          size="sm"
          class="audio-btn recording-pulse"
          v-b-tooltip.hover.left
          :title="`หยุดชั่วคราว (เหลือ ${formatTime(remainingTime)})`"
        >
          <i class="fas fa-pause"></i>
        </b-button>

        <b-button
          v-if="isRecording && isPaused"
          @click="resumeRecording"
          variant="info"
          size="sm"
          class="audio-btn"
          v-b-tooltip.hover.left
          :title="`บันทึกต่อ (เหลือ ${formatTime(remainingTime)})`"
        >
          <i class="fas fa-circle"></i>
        </b-button>

        <b-button
          v-if="isRecording"
          @click="stopRecording"
          variant="success"
          size="sm"
          class="audio-btn"
          v-b-tooltip.hover.left
          :title="`บันทึกสำเร็จ (เหลือ ${formatTime(remainingTime)})`"
        >
          <i class="fas fa-check"></i>
        </b-button>

        <b-button
          v-if="isRecording"
          @click="cancelRecording"
          variant="secondary"
          size="sm"
          class="audio-btn"
          v-b-tooltip.hover.left
          title="ยกเลิกการบันทึก"
        >
          <i class="fas fa-times"></i>
        </b-button>

        <b-button
          v-if="isTranscribing"
          variant="warning"
          size="sm"
          class="audio-btn"
          disabled
          v-b-tooltip.hover.left
          title="กำลังแปลงเสียงเป็นข้อความ..."
        >
          <b-spinner small></b-spinner>
        </b-button>
      </div>
    </div>

    <!-- Small show button when hidden -->
    <div 
      v-if="!isButtonVisible" 
      class="show-button-container position-absolute" 
      style="bottom: 5px; right: 5px"
    >
      <b-button
        @click="showButton"
        variant="outline-secondary"
        size="sm"
        class="show-audio-btn"
        v-b-tooltip.hover.left
        title="ขยายปุ่มบันทึกเสียง"
      >
        <i class="fas fa-microphone"></i>
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { isAIFeaturesEnabled } from "@/utils/environmentCheck";

// Constants
const RECORDING_TIME_LIMIT = 10 * 60 * 1000; // 10 minutes in milliseconds
const TIMER_UPDATE_INTERVAL = 100; // milliseconds
const AUDIO_CHUNK_INTERVAL = 1000; // milliseconds

// Transcription event types
const TRANSCRIPTION_EVENTS = {
  STARTED: 'transcription-started',
  CHUNK: 'transcription-chunk',
  COMPLETE: 'transcription-complete',
  ERROR: 'transcription-error',
  CANCELLED: 'transcription-cancelled'
};

export default {
  name: "AudioTranscribeButton",
  props: {
    value: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      // Recording state
      isRecording: false,
      isPaused: false,
      isTranscribing: false,
      isCancelled: false,
      
      // Audio handling
      mediaRecorder: null,
      audioChunks: [],
      
      // Transcription state
      streamingText: "",
      completionShown: false,
      originalTextLength: 0,
      
      // Timing
      recordingTimer: null,
      recordingStartTime: null,
      remainingTime: 0,
      displayTimer: null,

      // UI state
      showMenu: false,
      
      // Visibility state
      isButtonVisible: true,
    };
  },
  computed: {
    recordingTimeLimit() {
      return RECORDING_TIME_LIMIT;
    },
    isAIFeaturesEnabled() {
      return isAIFeaturesEnabled();
    }
  },
  created() {
    // Load button visibility from localStorage
    this.loadButtonVisibility();
  },
  methods: {
    // Safely map Vuex actions with fallback
    ...mapActions([
      // Try to map the action, but handle gracefully if module doesn't exist
    ]),

    async startRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        this.mediaRecorder = new MediaRecorder(stream, {
          mimeType: "audio/webm",
        });

        this.audioChunks = [];
        this.isRecording = true;
        this.isPaused = false;
        this.isCancelled = false;
        this.recordingStartTime = Date.now();
        this.remainingTime = this.recordingTimeLimit;
        this.startDisplayTimer();

        this.mediaRecorder.ondataavailable = event => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = () => {
          // Only process if we have audio chunks and recording wasn't cancelled
          if (this.audioChunks.length > 0 && !this.isCancelled) {
            this.processRecording();
          }
        };

        this.mediaRecorder.start(AUDIO_CHUNK_INTERVAL);

        // Set timer to auto-stop recording after 5 minutes
        this.recordingTimer = setTimeout(() => {
          if (this.isRecording) {
            this.stopRecording();
            this.$parent.$refs.dialog?.a(
              "warning",
              "การบันทึกเสียงหยุดอัตโนมัติเนื่องจากเกินเวลา 10 นาที"
            );
          }
        }, this.recordingTimeLimit);
      } catch (error) {
        this.$parent.$refs.dialog?.a(
          "danger",
          "ไม่สามารถเข้าถึงไมโครโฟนได้ กรุณาตรวจสอบการอนุญาต"
        );
      }
    },

    pauseRecording() {
      if (this.mediaRecorder && this.isRecording && !this.isPaused) {
        this.mediaRecorder.pause();
        this.isPaused = true;

        // Clear the recording timer when paused
        if (this.recordingTimer) {
          clearTimeout(this.recordingTimer);
          this.recordingTimer = null;
        }

        // Pause the display timer
        this.stopDisplayTimer();
      }
    },

    resumeRecording() {
      if (this.mediaRecorder && this.isRecording && this.isPaused) {
        this.mediaRecorder.resume();
        this.isPaused = false;

        // Restart the timer for remaining time when resumed
        const elapsedTime = Date.now() - this.recordingStartTime;
        const remainingTime = this.recordingTimeLimit - elapsedTime;

        if (remainingTime > 0) {
          this.recordingTimer = setTimeout(() => {
            if (this.isRecording) {
              this.stopRecording();
              this.$parent.$refs.dialog?.a(
                "warning",
                "การบันทึกเสียงหยุดอัตโนมัติเนื่องจากเกินเวลา 10 นาที"
              );
            }
          }, remainingTime);

          // Resume the display timer
          this.startDisplayTimer();
        } else {
          // Time already exceeded, stop immediately
          this.stopRecording();
          this.$parent.$refs.dialog?.a(
            "warning",
            "การบันทึกเสียงหยุดอัตโนมัติเนื่องจากเกินเวลา 5 นาที"
          );
        }
      }
    },

    stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
        this.mediaRecorder.stream.getTracks().forEach(track => track.stop());
        this.isRecording = false;
        this.isPaused = false;

        // Clear the recording timer
        if (this.recordingTimer) {
          clearTimeout(this.recordingTimer);
          this.recordingTimer = null;
        }

        // Stop the display timer
        this.stopDisplayTimer();
        this.remainingTime = 0;
      }
    },

    cancelRecording() {
      if (this.mediaRecorder && this.isRecording) {
        // Mark as cancelled first to prevent processing
        this.isCancelled = true;

        // Clear audio chunks
        this.audioChunks = [];

        // Stop recording - onstop will not process due to isCancelled flag
        this.mediaRecorder.stop();
        this.mediaRecorder.stream.getTracks().forEach(track => track.stop());
        this.isRecording = false;
        this.isPaused = false;

        // Clear the recording timer
        if (this.recordingTimer) {
          clearTimeout(this.recordingTimer);
          this.recordingTimer = null;
        }

        // Stop the display timer
        this.stopDisplayTimer();
        this.remainingTime = 0;

        // Emit cancellation event
        this.emitTranscriptionEvent(TRANSCRIPTION_EVENTS.CANCELLED);
        this.$parent.$refs.dialog?.a("info", "การบันทึกเสียงถูกยกเลิก");
      }
    },

    async processRecording() {
      if (this.audioChunks.length === 0) return;

      this.isTranscribing = true;
      this.streamingText = "";
      this.completionShown = false;
      this.originalTextLength = 0; // Always start fresh, don't append to existing text

      // Emit event when transcription processing starts
      this.emitTranscriptionEvent(TRANSCRIPTION_EVENTS.STARTED);

      try {
        const audioBlob = new Blob(this.audioChunks, { type: "audio/webm" });

        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result.split(",")[1];
          this.sendAudioForTranscription(
            base64Data,
            "recording.webm",
            "audio/webm"
          );
        };
        reader.readAsDataURL(audioBlob);
      } catch (error) {
        this.isTranscribing = false;
        this.$parent.$refs.dialog?.a(
          "danger",
          "เกิดข้อผิดพลาดในการประมวลผลเสียง"
        );
      }
    },

    async sendAudioForTranscription(audioData, fileName, mimeType) {
      try {
        // Try to get the Vuex action safely
        const processAction = this.$store.dispatch.bind(this.$store);

        if (
          processAction &&
          this.$store.hasModule &&
          this.$store.hasModule("moduleAudioAPI")
        ) {
          await processAction("moduleAudioAPI/processAudioBase64Stream", {
            audioData,
            fileName,
            mimeType,
            onChunk: data => this.handleTranscriptionChunk(data),
            onError: error => this.handleTranscriptionError(error),
          });
        } else {
          // Fallback: simulate transcription for development/testing
          console.warn(
            "Audio transcription module not available - using fallback"
          );
          this.simulateTranscription();
        }
      } catch (error) {
        console.error("Transcription error:", error);
        this.handleTranscriptionError(error);
      }
    },

    simulateTranscription() {
      // Fallback simulation for when the real API is not available
      this.isTranscribing = true;

      setTimeout(() => {
        const sampleText =
          "ตัวอย่างข้อความที่แปลงจากเสียง (Audio transcription service ยังไม่พร้อมใช้งาน)";
        this.handleTranscriptionChunk({
          type: "summary_chunk",
          data: sampleText,
        });

        setTimeout(() => {
          this.handleTranscriptionChunk({
            type: "complete",
          });
        }, 500);
      }, 2000);
    },

    handleTranscriptionChunk(data) {
      if (data.type === "summary_chunk") {
        // Only append the new chunk data
        this.streamingText += data.data;

        // Combine original text with streaming text
        const originalText = this.value.substring(0, this.originalTextLength);
        const separator = originalText.trim() ? "\n" : "";
        const newValue = originalText + separator + this.streamingText;

        this.$emit("input", newValue);
        this.emitTranscriptionEvent(TRANSCRIPTION_EVENTS.CHUNK, data);
      } else if (data.type === "complete") {
        this.handleTranscriptionCompletion();
      }
    },

    handleTranscriptionError(error) {
      this.isTranscribing = false;
      this.$parent.$refs.dialog?.a(
        "danger",
        "เกิดข้อผิดพลาดในการแปลงเสียงเป็นข้อความ: " + error
      );
      this.emitTranscriptionEvent(TRANSCRIPTION_EVENTS.ERROR, error);
    },

    // Transcription Helper Methods
    handleTranscriptionCompletion() {
      this.isTranscribing = false;

      if (!this.completionShown) {
        this.completionShown = true;
        this.$parent.$refs.dialog?.a(
          "success",
          "การแปลงเสียงเป็นข้อความเสร็จสิ้น"
        );
        this.emitTranscriptionEvent(TRANSCRIPTION_EVENTS.COMPLETE);
      }

      this.resetTranscriptionState();
    },

    resetTranscriptionState() {
      this.streamingText = "";
      this.originalTextLength = 0;
    },

    emitTranscriptionEvent(eventType, data = null) {
      this.$emit(eventType, data);
    },

    // Timer Management Methods
    startDisplayTimer() {
      this.displayTimer = setInterval(() => {
        if (this.isRecording && !this.isPaused) {
          const elapsedTime = Date.now() - this.recordingStartTime;
          this.remainingTime = Math.max(
            0,
            this.recordingTimeLimit - elapsedTime
          );

          if (this.remainingTime <= 0) {
            this.stopDisplayTimer();
          }
        }
      }, TIMER_UPDATE_INTERVAL);
    },

    stopDisplayTimer() {
      if (this.displayTimer) {
        clearInterval(this.displayTimer);
        this.displayTimer = null;
      }
    },

    formatTime(milliseconds) {
      const totalSeconds = Math.ceil(milliseconds / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    },

    // Simple Context Menu Methods
    showContextMenu() {
      this.showMenu = true;

      // Add click outside listener to close menu
      this.$nextTick(() => {
        document.addEventListener("click", this.handleClickOutside);
      });
    },

    handleClickOutside(event) {
      if (
        this.showMenu &&
        this.$refs.contextMenuOverlay &&
        !this.$refs.contextMenuOverlay.contains(event.target)
      ) {
        this.hideContextMenu();
      }
    },

    openHistory() {
      this.showMenu = false;
      this.$emit("open-history");
    },

    hideContextMenu() {
      this.showMenu = false;
      // Remove event listeners
      document.removeEventListener("click", this.handleClickOutside);
    },

    // Button Visibility Management
    loadButtonVisibility() {
      try {
        const saved = localStorage.getItem('audioTranscribeButtonVisible');
        if (saved !== null) {
          this.isButtonVisible = JSON.parse(saved);
        }
      } catch (error) {
        console.warn('Failed to load audio button visibility setting:', error);
        this.isButtonVisible = true; // Default to visible
      }
    },

    saveButtonVisibility() {
      try {
        localStorage.setItem('audioTranscribeButtonVisible', JSON.stringify(this.isButtonVisible));
      } catch (error) {
        console.warn('Failed to save audio button visibility setting:', error);
      }
    },

    toggleButtonVisibility() {
      this.isButtonVisible = !this.isButtonVisible;
      this.saveButtonVisibility();
      this.hideContextMenu();
      
      // Show appropriate message
      const message = this.isButtonVisible 
        ? "ขยายปุ่มบันทึกเสียงแล้ว" 
        : "ย่อขนาดปุ่มบันทึกเสียงแล้ว คุณสามารถขยายปุ่มได้อีกครั้งจากไอคอนเล็กที่มุมขวาล่าง";
      
      this.$parent.$refs.dialog?.a("info", message);
    },

    showButton() {
      this.isButtonVisible = true;
      this.saveButtonVisibility();
      this.$parent.$refs.dialog?.a("info", "ขยายปุ่มบันทึกเสียงแล้ว");
    },
  },

  beforeDestroy() {
    if (this.isRecording && this.mediaRecorder) {
      this.stopRecording();
    }

    // Clear any remaining timers
    if (this.recordingTimer) {
      clearTimeout(this.recordingTimer);
      this.recordingTimer = null;
    }

    // Clean up event listeners
    document.removeEventListener("click", this.handleClickOutside);

    this.stopDisplayTimer();
  },
};
</script>

<style scoped>
.audio-controls {
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.button-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
  max-width: 80px;
}

.timer-display {
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: bold;
  min-width: 40px;
  text-align: center;
  margin-bottom: 4px;
}

.timer-warning {
  background: rgba(220, 53, 69, 0.9);
  animation: timer-pulse 1s infinite;
}

@keyframes timer-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.audio-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.audio-btn i {
  margin: 0 !important;
  position: relative;
  top: 0;
  left: 0;
}

.audio-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.recording-pulse {
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
  }
}

/* Button Wrapper with Badge */
.audio-button-wrapper {
  position: relative;
  display: inline-block;
}

/* Original Button Styles */
.audio-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.audio-btn i {
  margin: 0 !important;
  position: relative;
  top: 0;
  left: 0;
}

.audio-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Badge Button Styles */
.menu-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #007bff;
  border: 2px solid white;
  color: white;
  font-size: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.menu-badge:hover {
  background: #0056b3;
  transform: scale(1.15);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
}

.menu-badge:active {
  transform: scale(0.9);
}

.menu-badge i {
  margin: 0;
  line-height: 1;
}

/* Context Menu Styles */

.context-menu {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  pointer-events: auto;
}

.context-menu-content {
  position: fixed;
  bottom: 80px;
  right: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
  min-width: 220px;
  overflow: hidden;
  animation: contextMenuSlideIn 0.2s ease-out;
  border: 1px solid #e9ecef;
  backdrop-filter: blur(10px);
}

@keyframes contextMenuSlideIn {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.context-menu-items {
  padding: 8px 0;
}

.context-menu-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  user-select: none;
}

.context-menu-item:hover {
  background-color: #f8f9fa;
}

.context-menu-item:active {
  background-color: #e9ecef;
}

.context-menu-item i {
  width: 18px;
  margin-right: 12px;
  text-align: center;
  font-size: 14px;
}

.context-menu-item span {
  font-size: 14px;
  color: #495057;
  white-space: nowrap;
}

/* Show Button Styles */
.show-button-container {
  z-index: 10;
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.show-button-container:hover {
  opacity: 1;
}

.show-audio-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #dee2e6;
}

.show-audio-btn i {
  margin: 0 !important;
  position: relative;
  top: 0;
  left: 0;
  color: #6c757d;
}

.show-audio-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  background: rgba(255, 255, 255, 1);
}

.show-audio-btn:hover i {
  color: #007bff;
}

/* Transition animations for hide/show */
.audio-controls {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.audio-controls.v-enter-active,
.audio-controls.v-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.audio-controls.v-enter,
.audio-controls.v-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.show-button-container.v-enter-active,
.show-button-container.v-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.show-button-container.v-enter,
.show-button-container.v-leave-to {
  opacity: 0;
  transform: scale(0.8);
}
</style>